import React, { useEffect, useState } from "react";
import { useSnackbar } from "@components";
import { Tooltip, Button } from "antd";
import type { Dayjs } from "dayjs";
import { workflowContainerFullById } from "../../../../../src/libs/apis";
import { WorkflowContainerFullI } from "../../../../../src/libs/interfaces";
import { FundViewOutlined } from "@ant-design/icons";
import {
  OneCard,
  OneVideo,
  ContainerDetailModal,
  InfoSectionI,
} from "../../../../../src/libs/shared";

const noShow = [
  "id",
  "client",
  "producer",
  "farm",
  "zone",
  "balerCode",
  "box",
  "brand",
  "container",
  "clientId",
  "producerId",
  "farmId",
  "magap",
  "boxId",
  "brandId",
  "closeProcess",
  "endLoading",
  "containerClose",
  "containerOulet",
  "numCajas",
  "cajasEstiba",
  "videoCajas",
  "videoCierreContenedor",
  "videoPanelExterior",
  "observation",
  "gancho",
  "ganchoCi",
  "calidad",
  "calidadCi",
  "date",
  "driver",
  "companyTransport",
  "dateVideoCajas",
  "dateVideoCierreContenedor",
  "dateVideoPanelExterior",
  "numKit",
  "placa",
  "timeStampSave",
  "magapOptional",
  "numCajasOptional",
  "cajasEstibaOptional",
  "boxOptional",
  "brandOptional",
  "boxOptionalId",
  "brandOptionalId",
  "producerOptional",
  "producerOptionalId",
  "farmOptional",
  "farmOptionalId",
  "farmOptionalZone",
  "balerCodeOptional",
];

const titles = {
  adhesivo: "Etiqueta Puerta Verificadora",
  adhesivoBisagra: "Etiqueta Bisagra",
  adhesivoPuerta: "Etiqueta Puerta Exportador",
  botellaExpor: "Sello Botella Exportador",
  botellaNaviera: "Sello Botella Naviera",
  botellaVerificadoraRastreo: "Sello Botella Verif.",
  cableExpor: "Sello Cable Exportador",
  cableVerificadora: "Sello Cable Verif.",
  evaluacionFruta: "Evaluación de Fruta",
  guiaTransporte: "Guía de Transporte",
  numRastreoSatelital: "Número de Rastreo",
  tarjaCarga: "Tarja de Carga",
  termografo: "Termógrafo",
  numKit: "Número de Kit",
  placa: "Placa Vehicular",
  ventoleraIzquierdaPhoto: "Ventolera Izquierda",
  ventoleraDerechaPhoto: "Ventolera Derecha",
  panelIzquierdoPhoto: "Foto de Panel Izquierdo",
  panelDerechoPhoto: "Foto de Panel Derecho",
  tarjaCargaPhotoOptional1:"Tarja de Carga Opcional 1",
  tarjaCargaPhotoOptional2:"Tarja de Carga Opcional 2",

} as any;

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
  showAlt?: boolean;
}

interface OneCardI {
  title: string;
  photos: OnePhotoI[];
  value?: string;
  noLine?: boolean;
}

interface ShowContainerPropsI {
  id: number;
  disabled?: boolean; // Añadir propiedad para deshabilitar en modo offline
}

export const ShowContainer = ({ id }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) {
      setOpen(true);
    }
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer open={open} onClose={handleClose} id={id} />
      )}
    </>
  );
};

const infoSections: InfoSectionI[] = [
  {
    title: "General",
    fields: [
      { key: "container", label: "Contenedor" },
      { key: "client", label: "Cliente" },
      { key: "producer", label: "Productor" },
      { key: "farm", label: "Finca" },
      { key: "zone", label: "Zona" },
      { key: "magap", label: "MAGAP" },
      { key: "balerCode", label: "Código Embaladora" },
      { key: "box", label: "Caja" },
      { key: "brand", label: "Marca" },
    ],
  },
  {
    title: "Tiempos",
    fields: [
      { key: "closeProcess", label: "Cierre proceso" },
      { key: "endLoading", label: "Fin carga" },
      { key: "containerClose", label: "Cierre contenedor" },
      { key: "containerOulet", label: "Salida contenedor" },
      { key: "hourSaveUser", label: "Hora usuario" },
      { key: "hourSaveServer", label: "Hora servidor" },
    ],
  },
  {
    title: "Carga",
    fields: [
      { key: "numCajas", label: "N° cajas" },
      { key: "cajasEstiba", label: "Cajas por estiba" },
      { key: "evaluacionFruta", label: "Evaluación de Fruta" },
      { key: "guiaTransporte", label: "Guía de Transporte" },
      { key: "numKit", label: "Número Kit" },
    ],
  },
  {
    title: "Sellos y etiquetas",
    fields: [
      { key: "adhesivo", label: "Etiqueta Puerta Verificadora" },
      { key: "adhesivoBisagra", label: "Etiqueta Bisagra" },
      { key: "adhesivoPuerta", label: "Etiqueta Puerta Exportador" },
      { key: "botellaExpor", label: "Sello Botella Exportador" },
      { key: "botellaNaviera", label: "Sello Botella Naviera" },
      { key: "botellaVerificadoraRastreo", label: "Sello Botella Verif." },
      { key: "cableExpor", label: "Sello Cable Exportador" },
      { key: "cableVerificadora", label: "Sello Cable Verif." },
    ],
  },
  {
    title: "Personal",
    fields: [
      { key: "driver", label: "Conductor" },
      { key: "ciConductor", label: "Identificación Conductor" },
      { key: "companyTransport", label: "Compañía Transporte" },
      { key: "placa", label: "Placa Vehicular" },
      { key: "gancho", label: "Gancho" },
      { key: "ganchoCi", label: "Identificación Gancho" },
      { key: "calidad", label: "Calidad" },
      { key: "calidadCi", label: "Identificación Calidad" },
    ],
  },
  {
    title: "Observaciones",
    fields: [
      { key: "observation", label: "Observación" },
    ],
  },
];

interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
}

export const ModalContainer = ({ open, onClose, id }: ModalContainerPropsI) => {
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState<WorkflowContainerFullI>({
    container: "",
  } as WorkflowContainerFullI);
  const [cards, setCards] = useState<OneCardI[]>([]);
  const [cardVideos, setCardVideos] = useState<OnePhotoI[]>([]);
  const [lasFotos, setLasFotos] = useState<string[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    handleGetData();
    return () => {
      setLoading(true);
      setData({
        container: "",
      } as WorkflowContainerFullI);
      setCards([]);
    };
  }, []);

  const handleGetData = async () => {
    try {
      const { data: dataContainer } = await workflowContainerFullById(id);
      if (!dataContainer) return;
      setData(dataContainer);

      const arrPhotosTotal: string[] = [];
      const arrKeys = Object.keys(dataContainer).filter(
        (el) => !noShow.includes(el)
      ) as Array<keyof WorkflowContainerFullI>;

      const keysParents = arrKeys
        .filter((el) => !el.includes("Photo"))
        .filter((el) => !el.includes("photo"))
        .filter((el) => !el.includes("video"));

      const arrCards = [];
      console.log(keysParents)
      for (const x of keysParents) {
        const obj: OneCardI = { title: titles[x] ?? x } as OneCardI;
        const arrPhotos = [];
        for (const y of arrKeys) {
          if (y !== x && y.includes(x)) {
            arrPhotos.push({
              src: dataContainer[y],
              alt: y,
            });
            arrPhotosTotal.push(dataContainer[y] as any);
          }
        }
        obj.photos = arrPhotos;
        obj.value = dataContainer[x] as string;
        arrCards.push(obj);
      }
      const objContainer = {
        title: "Contenedor",
        photos: [{ src: dataContainer["siglasFPhoto"], alt: "Contenedor" }],
      } as OneCardI;

      arrCards.unshift(objContainer);
      arrPhotosTotal.push(dataContainer["siglasFPhoto"] as any);

      const objCajasCon = {
        title: "Cajas Consolidadas",
        photos: [
          {
            src: dataContainer["cajasConsolidadasPhoto1"],
            alt: "cajasConsolidadasPhoto1",
          },
          {
            src: dataContainer["cajasConsolidadasPhoto2"],
            alt: "cajasConsolidadasPhoto2",
          },
          {
            src: dataContainer["cajasConsolidadasPhoto3"],
            alt: "cajasConsolidadasPhoto3",
          },
          {
            src: dataContainer["cajasConsolidadasPhoto4"],
            alt: "cajasConsolidadasPhoto4",
          },
          {
            src: dataContainer["cajasConsolidadasPhoto5"],
            alt: "cajasConsolidadasPhoto5",
          },
        ],
      };
      arrCards.push(objCajasCon);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto1"] as any);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto2"] as any);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto3"] as any);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto4"] as any);
      arrPhotosTotal.push(dataContainer["cajasConsolidadasPhoto5"] as any);

      const objCierreCon = {
        title: "Cierre de Contenedor",
        photos: [
          {
            src: dataContainer["cierreContenedorPhoto1"],
            alt: "cierreContenedorPhoto1",
          },
          {
            src: dataContainer["cierreContenedorPhoto2"],
            alt: "cierreContenedorPhoto2",
          },
          {
            src: dataContainer["cierreContenedorPhoto3"],
            alt: "cierreContenedorPhoto3",
          },
        ],
      };
      arrCards.push(objCierreCon);
      arrPhotosTotal.push(dataContainer["cierreContenedorPhoto1"] as any);
      arrPhotosTotal.push(dataContainer["cierreContenedorPhoto2"] as any);
      arrPhotosTotal.push(dataContainer["cierreContenedorPhoto3"] as any);

      const arrVideos = [
        { src: dataContainer.videoCajas, alt: "Video Cajas" },
        {
          src: dataContainer.videoCierreContenedor,
          alt: "Video Cierre Contenedor",
        },
        // { src: dataContainer.videoPanelExterior, alt: "Video Panel Exterior" },
      ];


      setCardVideos(arrVideos);
      setCards(arrCards);
      setLasFotos(arrPhotosTotal);
      setLoading(false);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
      setLoading(false);
    }
  };

  return (
    <ContainerDetailModal
      open={open}
      onClose={onClose}
      isLoading={isLoading}
      kind="CONTENEDOR FULL"
      container={data.container}
      data={data as any}
      sections={infoSections}
      gallery={
        <>
          {cards.map((el) => (
            <OneCard data={el} key={`${el.title}-card`} lasFotos={lasFotos} />
          ))}
          {cardVideos.map((el) => (
            <OneVideo {...el} key={`${el.alt}-video`} />
          ))}
        </>
      }
    />
  );
};
